import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { CircularProgress } from "@mui/material";

import { faClockRotateLeft } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";

import {
	useGetHistoryBySubtaskQuery,
	useGetHistoryByTaskQuery,
} from "@/pages/Private/redux/tasks-and-subtasks/task-and-subtasks-activity.api";

import { useGetAssigneesQuery } from "@/pages/Private/redux/tasks-and-subtasks/tasks-and-subtasks.api";
import { useGetAllTagsQuery } from "@/pages/Private/redux/to-do/to-do.api";

import { timeAgo } from "@/utils/timeAgo";

import { TaskAndSubtaskHistoryInfo } from "../schema/tasks-and-subtasks-activity";

interface HistoryProps {
	companyToDoProgressId: number | null;
	isTask: boolean;
}

/** Fields whose value is an id or an enum and needs looking up before display. */
const FIELD_ORDER = [
	"status",
	"priority",
	"assigneeId",
	"tagId",
	"title",
	"complexityEstimate",
	"description",
] as const;

export default function History({ companyToDoProgressId, isTask }: HistoryProps) {
	const { t } = useTranslation();
	const ts = (key: string, options?: Record<string, unknown>) => t(`to-do.${key}`, options ?? {});

	const { data: taskHistory, isLoading: isLoadingTask } = useGetHistoryByTaskQuery(
		companyToDoProgressId ?? 0,
		{ skip: !companyToDoProgressId || !isTask }
	);

	const { data: subtaskHistory, isLoading: isLoadingSubtask } = useGetHistoryBySubtaskQuery(
		companyToDoProgressId ?? 0,
		{ skip: !companyToDoProgressId || isTask }
	);

	const { data: users } = useGetAssigneesQuery();
	const { data: tags } = useGetAllTagsQuery();

	const userMap = useMemo(
		() =>
			(users ?? []).reduce<Record<number, string>>((acc, user) => {
				acc[+user.id] = `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim();

				return acc;
			}, {}),
		[users]
	);

	const tagMap = useMemo(
		() =>
			(tags ?? []).reduce<Record<number, string>>((acc, tag) => {
				if (tag.id) {
					acc[tag.id] = tag.name;
				}

				return acc;
			}, {}),
		[tags]
	);

	const entries = useMemo(() => {
		const raw = (isTask ? taskHistory : subtaskHistory) ?? [];

		// Newest first — the API returns insertion order.
		return [...raw].sort(
			(a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime()
		);
	}, [isTask, taskHistory, subtaskHistory]);

	const isLoading = isTask ? isLoadingTask : isLoadingSubtask;

	const headline = (entry: TaskAndSubtaskHistoryInfo) =>
		ts(`activityLog.${entry.type}.${entry.action}`, {
			user: userMap[entry.userId] || ts("activityLog.unknownUser"),
			entity: isTask ? ts("activityLog.entityTask") : ts("activityLog.entitySubtask"),
		});

	/** An id or enum means nothing on screen; a name or a translated label does. */
	const formatValue = (field: string, value: unknown): string => {
		if (value === null || value === undefined || value === "") {
			return ts("activityLog.empty");
		}

		if (field === "status" || field === "priority") {
			return ts(`${value}`);
		}

		if (field === "assigneeId") {
			return userMap[Number(value)] || t("createTask.unassigned");
		}

		if (field === "tagId") {
			return tagMap[Number(value)] || ts("none");
		}

		if (field === "description") {
			// Descriptions are paragraphs; the change is the point, not the text.
			return ts("activityLog.textChanged");
		}

		return `${value}`;
	};

	const changedFields = (entry: TaskAndSubtaskHistoryInfo) => {
		const next = entry.newData ?? {};
		const previous = entry.oldData ?? {};
		const names = Object.keys(next).length ? Object.keys(next) : Object.keys(previous);

		return names
			.sort((a, b) => {
				const orderOf = (field: string) => {
					const index = FIELD_ORDER.indexOf(field as (typeof FIELD_ORDER)[number]);

					return index === -1 ? FIELD_ORDER.length : index;
				};

				return orderOf(a) - orderOf(b);
			})
			.map((field) => ({
				field,
				label: ts(`activityLog.fields.${field}`),
				from: formatValue(field, previous[field]),
				to: Object.keys(next).length ? formatValue(field, next[field]) : null,
			}));
	};

	if (isLoading) {
		return (
			<div className="w-full flex justify-center py-6">
				<CircularProgress size={20} />
			</div>
		);
	}

	if (!entries.length) {
		return <p className="text-[14px] leading-5 text-subtle py-2">{ts("noHistory")}</p>;
	}

	return (
		<ol className="w-full flex flex-col">
			{entries.map((entry, index) => {
				const fields = changedFields(entry);

				return (
					<li key={entry.id ?? index} className="flex gap-3">
						{/* The rail: a dot per entry, a line between them. */}
						<div className="flex flex-col items-center">
							<span className="mt-1.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-surface">
								<Icon className="w-3 h-3 text-subtle" icon={faClockRotateLeft} />
							</span>
							{index < entries.length - 1 ? <span className="w-px flex-1 bg-border" /> : null}
						</div>
						<div className="pb-4 pt-1.5 min-w-0">
							<p className="text-[14px] leading-5 text-primary-blue-dark">{headline(entry)}</p>
							{fields.length ? (
								<ul className="mt-1 flex flex-col gap-0.5">
									{fields.map(({ field, label, from, to }) => (
										<li key={field} className="text-[13px] leading-5 text-subtle">
											<span className="font-semibold">{label}:</span>{" "}
											{to === null ? (
												from
											) : (
												<>
													<span className="line-through">{from}</span>
													{" → "}
													<span className="text-primary-blue-dark">{to}</span>
												</>
											)}
										</li>
									))}
								</ul>
							) : null}
							{entry.createdAt ? (
								<p className="mt-1 text-[12px] leading-4 text-subtle">{timeAgo(entry.createdAt)}</p>
							) : null}
						</div>
					</li>
				);
			})}
		</ol>
	);
}
